import { useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Mail, ShieldCheck, Search, RefreshCw, Send,
  CheckCircle2, XCircle, AlertTriangle, Loader2, Eye, Copy,
} from "lucide-react";
import "./AdminEmailDelivery.css";

// --- helpers ----------------------------------------------------------------

function KPI({ label, value, sub, testid }) {
  return (
    <div className="email-infra-kpi" data-testid={testid}>
      <div className="email-infra-kpi__label">{label}</div>
      <div className="email-infra-kpi__value">{value}</div>
      {sub ? <div className="email-infra-kpi__sub">{sub}</div> : null}
    </div>
  );
}

function Pill({ tone = "neutral", children, testid }) {
  return (
    <span className={`email-infra-pill pill-${tone}`} data-testid={testid}>
      {children}
    </span>
  );
}

function fmtPct(n) { return n == null ? "—" : `${Number(n).toFixed(1)}%`; }
function fmtTs(t) { return t ? new Date(t).toLocaleString() : "—"; }
function truncate(s, n = 64) { return !s ? "" : (s.length <= n ? s : s.slice(0, n) + "…"); }

// --- Overview tab -----------------------------------------------------------

function OverviewTab({ stats, health, reload }) {
  if (!stats || !health) return null;
  const b = stats.buckets["24h"];
  const b7 = stats.buckets["7d"];
  const b30 = stats.buckets["30d"];
  const domainTone = health.domain_status_label === "verified" ? "verified"
    : health.domain_status_label === "partial" ? "warning" : "error";
  return (
    <div>
      <div className="email-infra-controls">
        <button className="email-infra-btn email-infra-btn--secondary"
                onClick={reload} data-testid="email-refresh-btn">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      <div className="email-infra-kpi-grid">
        <KPI label="Provider" value={stats.provider} sub={stats.configured ? "Configured" : "Not configured"}
             testid="kpi-provider" />
        <KPI label="Sending Domain" value={stats.sending_domain || "—"} sub={stats.sender}
             testid="kpi-sending-domain" />
        <KPI label="Domain Status" value={health.domain_status_label.toUpperCase()}
             sub={`${health.domains_verified}/${health.domains_total} verified`}
             testid="kpi-domain-status" />
        <KPI label="Delivery Success (24h)" value={fmtPct(b.delivery_rate_pct)}
             sub={`${b.sent} sent · ${b.failed} failed`} testid="kpi-delivery-24h" />
        <KPI label="Bounce Rate (24h)" value={fmtPct(b.bounce_rate_pct)}
             sub={`${b.bounced} bounced`} testid="kpi-bounce-24h" />
        <KPI label="Complaint Rate (24h)" value={fmtPct(b.complaint_rate_pct)}
             sub={`${b.complained} complained`} testid="kpi-complaint-24h" />
      </div>

      <div className="email-infra-card" data-testid="rolling-stats-card">
        <div className="email-infra-card__title"><Mail size={16} /> Rolling Delivery Statistics</div>
        <table className="email-infra-table">
          <thead>
            <tr><th>Window</th><th>Attempts</th><th>Sent</th><th>Delivered</th><th>Bounced</th><th>Complained</th><th>Failed</th><th>Delivery %</th><th>Bounce %</th></tr>
          </thead>
          <tbody>
            {[["24h", b], ["7 days", b7], ["30 days", b30]].map(([label, x]) => (
              <tr key={label}>
                <td><strong>{label}</strong></td>
                <td>{x.attempts}</td><td>{x.sent}</td><td>{x.delivered || x.sent}</td>
                <td>{x.bounced}</td><td>{x.complained}</td><td>{x.failed}</td>
                <td><Pill tone={x.delivery_rate_pct >= 95 ? "verified" : x.delivery_rate_pct >= 80 ? "warning" : "error"}>{fmtPct(x.delivery_rate_pct)}</Pill></td>
                <td><Pill tone={x.bounce_rate_pct <= 2 ? "verified" : x.bounce_rate_pct <= 5 ? "warning" : "error"}>{fmtPct(x.bounce_rate_pct)}</Pill></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="email-infra-card">
        <div className="email-infra-card__title"><CheckCircle2 size={16} /> Latest Activity</div>
        <table className="email-infra-table">
          <thead><tr><th>Kind</th><th>To</th><th>Subject</th><th>Source</th><th>At</th></tr></thead>
          <tbody>
            {stats.last_success ? (
              <tr data-testid="latest-success">
                <td><Pill tone="verified">SUCCESS</Pill></td>
                <td>{stats.last_success.to}</td>
                <td>{truncate(stats.last_success.subject, 60)}</td>
                <td><code>{stats.last_success.source}</code></td>
                <td>{fmtTs(stats.last_success.ts)}</td>
              </tr>
            ) : null}
            {stats.last_failure ? (
              <tr data-testid="latest-failure">
                <td><Pill tone="error">FAILED</Pill></td>
                <td>{stats.last_failure.to}</td>
                <td>{truncate(stats.last_failure.subject, 60)}</td>
                <td><code>{stats.last_failure.source}</code></td>
                <td>{fmtTs(stats.last_failure.ts)}</td>
              </tr>
            ) : null}
            {!stats.last_success && !stats.last_failure ? (
              <tr><td colSpan={5} style={{ color: "#64748B" }}>No activity yet.</td></tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// --- Domains tab ------------------------------------------------------------

function DomainsTab({ domains, reload }) {
  if (!domains) return null;
  return (
    <div>
      <div className="email-infra-controls">
        <button className="email-infra-btn email-infra-btn--secondary" onClick={reload}
                data-testid="email-domains-refresh-btn">
          <RefreshCw size={14} /> Refresh Domains
        </button>
      </div>
      {!domains.ok ? (
        <div className="email-infra-card" data-testid="domains-error">
          <div className="email-infra-card__title"><AlertTriangle size={16} /> Resend not configured</div>
          <p style={{ color: "#64748B", fontSize: 13 }}>{domains.error || "Configure RESEND_API_KEY to see live domain authentication status."}</p>
        </div>
      ) : (domains.domains || []).map(d => (
        <div key={d.id} className="email-infra-card" data-testid={`domain-card-${d.name}`}>
          <div className="email-infra-card__title">
            <ShieldCheck size={16} /> {d.name}
            <Pill tone={d.status === "verified" ? "verified" : "warning"}>{d.status}</Pill>
            {d.sending_enabled ? <Pill tone="verified">Sending Enabled</Pill> : <Pill tone="error">Sending Disabled</Pill>}
          </div>
          <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
            <Pill tone={d.spf_verified ? "verified" : "error"}>SPF {d.spf_verified ? "✓" : "✗"}</Pill>
            <Pill tone={d.dkim_verified ? "verified" : "error"}>DKIM {d.dkim_verified ? "✓" : "✗"}</Pill>
            <Pill tone={d.dmarc_verified ? "verified" : "warning"}>DMARC {d.dmarc_verified ? "✓" : "not set"}</Pill>
            <Pill tone="neutral">Region: {d.region}</Pill>
          </div>
          <table className="email-infra-table">
            <thead><tr><th>Record</th><th>Type</th><th>Name</th><th>Value</th><th>Status</th></tr></thead>
            <tbody>
              {(d.records || []).map((r, idx) => (
                <tr key={idx}>
                  <td><strong>{r.record}</strong></td>
                  <td>{r.type}</td>
                  <td><code>{r.name}</code></td>
                  <td><code style={{ fontSize: 11 }}>{truncate(r.value, 60)}</code></td>
                  <td><Pill tone={r.status === "verified" ? "verified" : "warning"}>{r.status}</Pill></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

// --- Activity tab -----------------------------------------------------------

function ActivityTab() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [source, setSource] = useState("");
  const [days, setDays] = useState(7);
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ since_days: days, limit: 100 });
      if (q) params.set("q", q);
      if (status) params.set("status", status);
      if (source) params.set("source", source);
      const { data } = await api.get(`/admin/email/activity?${params}`);
      setRows(data.items || []); setTotal(data.total);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, [q, status, source, days]);

  useEffect(() => {
    api.get("/admin/email/sources").then(({ data }) => setSources(data.items || []));
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="email-infra-controls">
        <input placeholder="Search to / subject / id…" value={q}
               onChange={e => setQ(e.target.value)} data-testid="activity-q-input" />
        <select value={status} onChange={e => setStatus(e.target.value)} data-testid="activity-status-select">
          <option value="">All statuses</option>
          <option value="ok">Sent (ok)</option>
          <option value="failed">Failed</option>
          <option value="delivered">Delivered</option>
          <option value="bounced">Bounced</option>
          <option value="complained">Complained</option>
        </select>
        <select value={source} onChange={e => setSource(e.target.value)} data-testid="activity-source-select">
          <option value="">All sources</option>
          {Array.from(new Map(sources.map(s => [s.source, s])).values()).map(s => (
            <option key={s.source} value={s.source}>{s.source}</option>
          ))}
        </select>
        <select value={days} onChange={e => setDays(Number(e.target.value))} data-testid="activity-days-select">
          <option value={1}>Last 24h</option>
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
        <button className="email-infra-btn email-infra-btn--primary" onClick={load}
                disabled={loading} data-testid="activity-search-btn">
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
          Search
        </button>
      </div>
      <div className="email-infra-card">
        <div className="email-infra-card__title">
          <Mail size={16} /> Activity ({rows.length} of {total})
        </div>
        <table className="email-infra-table" data-testid="activity-table">
          <thead><tr><th>Status</th><th>To</th><th>Subject</th><th>Source</th><th>Delivery</th><th>Sent</th></tr></thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={6} style={{ color: "#64748B" }}>No activity matched.</td></tr>
            ) : rows.map((r, idx) => (
              <tr key={idx} data-testid={`activity-row-${idx}`}>
                <td>
                  <Pill tone={r.ok ? "verified" : "error"}>{r.ok ? "SENT" : "FAILED"}</Pill>
                </td>
                <td>{r.to}</td>
                <td>{truncate(r.subject, 60)}</td>
                <td><code>{r.source}</code></td>
                <td>{r.delivery_status ? <Pill tone={r.delivery_status === "delivered" ? "verified" : r.delivery_status === "bounced" ? "error" : "warning"}>{r.delivery_status}</Pill> : <span style={{ color: "#94A3B8" }}>—</span>}</td>
                <td>{fmtTs(r.ts)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// --- Templates tab ----------------------------------------------------------

function TemplatesTab() {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [testEmail, setTestEmail] = useState("");
  const [customVars, setCustomVars] = useState("");
  const [sending, setSending] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    api.get("/admin/email/templates").then(({ data }) => setTemplates(data.items || []));
  }, []);

  const openTemplate = async (t) => {
    setSelected(t); setDetail(null);
    setTestEmail(""); setCustomVars(JSON.stringify(t.sample_vars || {}, null, 2));
    try {
      const { data } = await api.get(`/admin/email/templates/${t.id}`);
      setDetail(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const rerender = async () => {
    if (!selected) return;
    setPreviewing(true);
    try {
      const vars = customVars ? JSON.parse(customVars) : {};
      const { data } = await api.post(`/admin/email/templates/${selected.id}/preview`, { variables: vars });
      setDetail(d => ({ ...(d || {}), preview_html: data.html, preview_subject: data.subject }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Preview failed");
    } finally { setPreviewing(false); }
  };

  const testSend = async () => {
    if (!selected || !testEmail) { toast.error("Enter a recipient email."); return; }
    setSending(true);
    try {
      const vars = customVars ? JSON.parse(customVars) : {};
      const { data } = await api.post(`/admin/email/templates/${selected.id}/test-send`, {
        to_email: testEmail, variables: vars,
      });
      toast.success(`Test send OK · Resend id ${data.resend_id?.slice(0, 8) || "sent"}…`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Send failed");
    } finally { setSending(false); }
  };

  const grouped = useMemo(() => {
    const g = {};
    templates.forEach(t => { (g[t.category] ||= []).push(t); });
    return g;
  }, [templates]);

  return (
    <div>
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        gap: 12, marginBottom: 14, padding: "12px 14px", background: "#F0F5FF",
        border: "1px solid #C7D2FE", borderRadius: 10,
      }}>
        <div style={{ color: "#1E3A8A", fontSize: 13 }}>
          <strong>Read-only catalog view.</strong> To edit templates with WYSIWYG, approval workflow, and versioning →{" "}
        </div>
        <a href="/admin/email-templates" data-testid="templates-open-manager"
           style={{ background: "#1E3A8A", color: "#fff", padding: "8px 14px",
                    borderRadius: 999, textDecoration: "none", fontSize: 13,
                    fontWeight: 600 }}>
          Open Template Manager →
        </a>
      </div>
      <div style={{ color: "#64748B", fontSize: 13, marginBottom: 12 }}>
        {templates.length} embedded templates. This is a <strong>read-only catalog</strong> — the actual sending code
        lives in <code>email_service.py</code> and the partner modules and is not modified here.
      </div>
      {Object.entries(grouped).map(([cat, items]) => (
        <div key={cat} style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "#F59E0B", fontWeight: 700, margin: "8px 0" }}>
            {cat}
          </div>
          <div className="email-infra-template-list">
            {items.map(t => (
              <div key={t.id} className="email-infra-template-card"
                   onClick={() => openTemplate(t)} data-testid={`template-card-${t.id}`}>
                <div className="email-infra-template-card__cat">{t.source_label}</div>
                <div className="email-infra-template-card__name">{t.name}</div>
                <div className="email-infra-template-card__purpose">{t.purpose}</div>
                <div className="email-infra-template-card__meta">
                  <Pill tone="neutral">{t.variables.length} vars</Pill>
                  <Pill tone="neutral">{t.module}</Pill>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {selected ? (
        <>
          <div className="email-infra-drawer-backdrop" onClick={() => { setSelected(null); setDetail(null); }} />
          <div className="email-infra-drawer" data-testid="template-drawer">
            <button className="email-infra-drawer__close" onClick={() => { setSelected(null); setDetail(null); }}>
              ✕
            </button>
            <div style={{ marginBottom: 8, fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: "#F59E0B", fontWeight: 700 }}>
              {selected.category} · {selected.source_label}
            </div>
            <h2 style={{ margin: 0, fontSize: 22, color: "#0F172A" }}>{selected.name}</h2>
            <p style={{ color: "#64748B", fontSize: 13 }}>{selected.purpose}</p>
            <div style={{ margin: "10px 0", padding: "8px 12px", background: "#F9F8F6", borderRadius: 8, fontSize: 12 }}>
              <strong>Module:</strong> <code>{selected.module}</code>
            </div>

            <div className="email-infra-card">
              <div className="email-infra-card__title">Variables</div>
              <table className="email-infra-table">
                <thead><tr><th>Name</th><th>Type</th><th>Required</th></tr></thead>
                <tbody>
                  {selected.variables.map(v => (
                    <tr key={v.name}>
                      <td><code>{v.name}</code></td>
                      <td>{v.type}</td>
                      <td>{v.required ? <Pill tone="verified">yes</Pill> : <Pill tone="neutral">no</Pill>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="email-infra-card">
              <div className="email-infra-card__title"><Eye size={16} /> Variables (JSON)</div>
              <textarea
                value={customVars}
                onChange={e => setCustomVars(e.target.value)}
                rows={8}
                style={{ width: "100%", fontFamily: "monospace", fontSize: 12, padding: 10, borderRadius: 8, border: "1px solid #CBD5E1" }}
                data-testid="template-vars-textarea"
              />
              <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button className="email-infra-btn email-infra-btn--secondary" onClick={rerender}
                        disabled={previewing} data-testid="template-rerender-btn">
                  {previewing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Re-render preview
                </button>
                <button className="email-infra-btn email-infra-btn--secondary"
                        onClick={() => { setCustomVars(JSON.stringify(selected.sample_vars || {}, null, 2)); }}>
                  <Copy size={14} /> Reset to sample
                </button>
              </div>
            </div>

            <div className="email-infra-card">
              <div className="email-infra-card__title"><Send size={16} /> Test Send</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                <input
                  type="email" placeholder="recipient@example.com"
                  value={testEmail} onChange={e => setTestEmail(e.target.value)}
                  style={{ minWidth: 260, padding: 8, borderRadius: 8, border: "1px solid #CBD5E1" }}
                  data-testid="template-test-email-input"
                />
                <button className="email-infra-btn email-infra-btn--primary"
                        onClick={testSend} disabled={sending || !testEmail}
                        data-testid="template-test-send-btn">
                  {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  Send test to this address
                </button>
              </div>
              <p style={{ fontSize: 11, color: "#64748B", marginTop: 8 }}>
                Subject will be prefixed with <code>[TEST]</code>. The send is recorded to <code>email_logs</code>
                with source <code>admin_test_send:{selected.id}</code>.
              </p>
            </div>

            <div className="email-infra-card">
              <div className="email-infra-card__title">Preview</div>
              <div style={{ fontSize: 12, color: "#64748B", marginBottom: 8 }}>
                Subject: <strong style={{ color: "#0F172A" }}>{detail?.preview_subject || "—"}</strong>
              </div>
              <iframe
                title="Template preview"
                srcDoc={detail?.preview_html || ""}
                className="email-infra-preview-frame"
                sandbox=""
                data-testid="template-preview-iframe"
              />
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

// --- Main -------------------------------------------------------------------

export default function AdminEmailDelivery() {
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [health, setHealth] = useState(null);
  const [domains, setDomains] = useState(null);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const results = await Promise.allSettled([
        api.get("/admin/email/stats"),
        api.get("/admin/email/health"),
        api.get("/admin/email/domains"),
      ]);
      const [s, h, d] = results;
      if (s.status === "fulfilled") setStats(s.value.data);
      if (h.status === "fulfilled") setHealth(h.value.data);
      if (d.status === "fulfilled") setDomains(d.value.data);
      const failed = results.filter(r => r.status === "rejected");
      if (failed.length && failed.length === results.length) {
        toast.error(formatApiError(failed[0].reason));
      }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  return (
    <div className="email-infra-shell" data-testid="admin-email-delivery">
      <div className="email-infra-hero">
        <div className="email-infra-hero__eyebrow">Administration · Email Infrastructure</div>
        <h1 className="email-infra-hero__title">Email Delivery Dashboard</h1>
        <div className="email-infra-hero__sub">
          Live Resend provider health, activity history, embedded template catalog, and DNS authentication status.
        </div>
      </div>

      <div className="email-infra-tabs" data-testid="email-infra-tabs">
        {[
          ["overview", "Overview"],
          ["activity", "Activity History"],
          ["templates", "Template Catalog"],
          ["domains", "Domain Health"],
        ].map(([k, label]) => (
          <button
            key={k}
            className={`email-infra-tab ${tab === k ? "email-infra-tab--active" : ""}`}
            onClick={() => setTab(k)}
            data-testid={`email-tab-${k}`}
          >
            {label}
          </button>
        ))}
      </div>

      {loading && !stats ? (
        <div style={{ color: "#64748B", padding: 40, textAlign: "center" }}>
          <Loader2 size={22} className="animate-spin" style={{ marginRight: 8 }} />
          Loading email infrastructure…
        </div>
      ) : (
        <>
          {tab === "overview" && <OverviewTab stats={stats} health={health} reload={reload} />}
          {tab === "activity" && <ActivityTab />}
          {tab === "templates" && <TemplatesTab />}
          {tab === "domains" && <DomainsTab domains={domains} reload={reload} />}
        </>
      )}
    </div>
  );
}
